import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { listCaseNotes } from "@/features/cases/api";
import { CaseStatusBadge } from "@/features/cases/components/case-status-badge";
import { useCases } from "@/features/cases/hooks/use-cases";
import { type CaseNote, isCaseDone } from "@/features/cases/types";
import { ResolutionJourney } from "@/components/common/resolution-journey";
import { ClientMemberOnboardingCard } from "@/features/clients/components/client-member-onboarding-card";
import { ClientTagsCard } from "@/features/clients/components/client-tags-card";
import { ClientTeamCard } from "@/features/clients/components/client-team-card";
import { PortalAccessCard } from "@/features/clients/components/portal-access-card";
import { cn, formatCents } from "@/lib/utils";
import { useActiveClient } from "@/layouts/client-layout";

function formatWhen(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function dueLabel(days: number | null): string {
  if (days === null) return "—";
  if (days === 0) return "Today";
  if (days < 0) return `Overdue ${-days}d`;
  return `In ${days}d`;
}

export default function ClientOverviewPage() {
  const { client, onClientUpdated } = useActiveClient();
  const { cases, loading: casesLoading } = useCases(client.id, "all");
  const [notes, setNotes] = useState<CaseNote[]>([]);

  const openCases = cases.filter((c) => !isCaseDone(c.status));
  // Best-effort resolution-journey stage from available signals. No dedicated
  // "stage" field exists yet, so derive a plausible step from case activity.
  // TODO(api): replace with a real per-case resolution stage when the backend
  // exposes one.
  const journeyStep = openCases.length > 0 ? 3 : cases.length > 0 ? 4 : 1;
  // Only count open steps on still-open cases — a closed case can retain
  // unchecked steps (completing a case doesn't auto-complete them).
  const openSteps = openCases.reduce((n, c) => n + c.openStepCount, 0);
  // Earliest upcoming/overdue due date across the open cases.
  const nextDue = openCases
    .map((c) => c.daysUntilDue)
    .filter((d): d is number => d !== null)
    .sort((a, b) => a - b)[0];

  // Recent activity: newest few notes across all of this client's cases.
  // A client has only a handful of cases, so this stays a small fan-out.
  useEffect(() => {
    if (cases.length === 0) {
      setNotes([]);
      return;
    }
    let active = true;
    Promise.all(cases.map((c) => listCaseNotes(c.id).catch(() => [])))
      .then((lists) => {
        if (!active) return;
        const merged = lists
          .flat()
          .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
          .slice(0, 6);
        setNotes(merged);
      })
      .catch(() => active && setNotes([]));
    return () => {
      active = false;
    };
  }, [cases]);

  const snapshot = [
    { label: "Open cases", value: casesLoading ? "—" : String(openCases.length) },
    { label: "Open tasks", value: casesLoading ? "—" : String(openSteps) },
    {
      label: "Next due",
      value: casesLoading ? "—" : dueLabel(nextDue ?? null),
      muted: !casesLoading && (nextDue ?? null) === null,
    },
    { label: "Balance due", value: formatCents(client.balanceCents) },
  ];

  return (
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
      {/* Left (wide) */}
      <div className="flex flex-col gap-6 lg:col-span-8">
        {/* Snapshot band */}
        <Card className="grid grid-cols-2 gap-0 overflow-hidden p-0 sm:grid-cols-4">
          {snapshot.map((cell, i) => (
            <div
              key={cell.label}
              className={cn(
                "flex flex-col gap-2 px-6 py-5",
                i > 0 && "border-t sm:border-t-0",
                i < snapshot.length - 1 && "sm:border-r",
              )}
            >
              <span className="text-muted-foreground text-xs font-semibold tracking-[0.08em] uppercase">
                {cell.label}
              </span>
              <span
                className={cn(
                  "font-heading text-4xl leading-none font-semibold tracking-[-0.03em] tabular-nums",
                  cell.muted && "text-border",
                )}
              >
                {cell.value}
              </span>
            </div>
          ))}
        </Card>

        {/* Resolution journey */}
        <ResolutionJourney currentStep={journeyStep} />

        {/* Open cases */}
        <Card className="gap-5 p-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">Open cases</h2>
            <Link
              to={`/client/${client.id}/cases`}
              className="text-info text-[13px] font-semibold hover:underline"
            >
              All cases
            </Link>
          </div>
          {casesLoading ? (
            <p className="text-muted-foreground py-4 text-sm">Loading…</p>
          ) : openCases.length === 0 ? (
            <div className="border-border bg-surface-hover flex flex-wrap items-center gap-5 rounded-lg border border-dashed px-6 py-5">
              <svg width="52" height="44" viewBox="0 0 64 48" fill="none" aria-hidden="true" className="shrink-0">
                <rect x="6" y="10" width="52" height="34" rx="6" stroke="#2457E6" strokeOpacity="0.35" strokeWidth="2" />
                <rect x="14" y="4" width="36" height="34" rx="6" fill="#FFFFFF" stroke="#2457E6" strokeWidth="2" />
                <path d="M24 16h16M24 23h12" stroke="#2457E6" strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[15px] font-semibold">No open cases for this client</span>
                <span className="text-muted-foreground text-[13px]">
                  Open a case to track notices, deadlines and tasks against this client.
                </span>
              </div>
              <Button asChild variant="outline" className="border-info/60 text-info hover:text-info rounded-[11px]">
                <Link to={`/client/${client.id}/cases`}>Create a case</Link>
              </Button>
            </div>
          ) : (
            <ul className="-mx-1 divide-y">
              {openCases.map((c) => (
                <li key={c.id} className="flex items-center justify-between px-1 py-3">
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/client/${client.id}/cases/${c.id}`}
                      className="text-foreground hover:text-primary text-sm font-medium hover:underline"
                    >
                      {c.resolutionTypeDisplay}
                    </Link>
                    <CaseStatusBadge status={c.status} label={c.statusDisplay} />
                  </div>
                  <div className="text-muted-foreground flex items-center gap-4 text-xs">
                    <span>{c.openStepCount} open steps</span>
                    <span
                      className={
                        c.daysUntilDue !== null && c.daysUntilDue < 0
                          ? "text-destructive font-medium"
                          : ""
                      }
                    >
                      {dueLabel(c.daysUntilDue)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Recent activity */}
        <Card className="gap-4 p-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">Recent activity</h2>
            <span className="text-muted-foreground text-xs font-semibold">Last 30 days</span>
          </div>
          <ol className="flex flex-col">
            {notes.map((note, i) => (
              <li key={note.id} className="flex gap-3.5 py-2.5">
                <span className="flex shrink-0 flex-col items-center">
                  <span className="bg-info ring-accent size-3 rounded-full ring-4" />
                  {i < notes.length - 1 && <span className="bg-border mt-1 w-0.5 flex-grow" />}
                </span>
                <span className="flex flex-col gap-0.5 pb-2 leading-snug">
                  <span className="text-sm font-semibold">
                    {note.isSystem ? <span className="font-medium italic">{note.text}</span> : note.text}
                  </span>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {note.isSystem ? "Activity" : note.authorName || "Note"} · {formatWhen(note.createdAt)}
                  </span>
                </span>
              </li>
            ))}
            <li className="flex gap-3.5 py-2.5">
              <span className="border-border size-3 shrink-0 rounded-full border-[3px] bg-card" />
              <span className="text-muted-foreground text-sm font-medium">
                {notes.length === 0
                  ? "No recent activity yet — documents, notices and signatures will show up here."
                  : "Nothing else yet — documents, notices and signatures will show up here."}
              </span>
            </li>
          </ol>
        </Card>
      </div>

      {/* Right column */}
      <div className="flex flex-col gap-6 lg:col-span-4">
        <ClientMemberOnboardingCard clientId={client.id} />
        <PortalAccessCard clientId={client.id} />
        <ClientTeamCard client={client} onClientUpdated={onClientUpdated} />
        <ClientTagsCard client={client} onClientUpdated={onClientUpdated} />
      </div>
    </div>
  );
}
